import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BedDouble, Bath, Ruler, ArrowUpRight, Clock3, Calculator } from "lucide-react";
import { api } from "@/lib/api";

const LIVE_LISTINGS_URL =
  "https://www.sellwithjoselle.com/mapsearchapp/search/eyJzZWFyY2giOnsic2VhcmNoVHlwZSI6InJlc2lkZW50aWFsIiwibGlzdGluZ1R5cGUiOlsiU2FsZSJdLCJvcGVuSG91c2UiOnsiZnJvbSI6MCwidG8iOjB9LCJiZWQiOjAsIm1hcmtldGRheXMiOjAsImJhdGgiOjAsInNlYXJjaEJ5Ijoic2VhcmNoYWxsIiwic2VhcmNoQnlUZXh0IjoiTGFnb29uIENpdHksIE9OLCBDYW5hZGEiLCJwcmljZVJhbmdlIjp7Im1pbiI6MCwibWF4IjowfSwiZmVldFJhbmdlIjp7Im1pbiI6MCwibWF4IjowfSwic2hvd09ubHkiOltdLCJwcmljZURyb3AiOmZhbHNlLCJwb3dlck9mU2FsZSI6ZmFsc2UsInNvcnRieSI6Im5ld2VzdCIsImNvbmRvVHlwZSI6IiIsImNvbmRvT2NjdXBhbmN5IjoiIiwiY29uZG9TdGF0dXMiOiIiLCJjb25kb0J1aWxkZXIiOiIiLCJrZXl3b3JkcyI6W10sIlBvc3RhbENvZGUiOmZhbHNlLCJQcm92aW5jZSI6ZmFsc2UsIkNpdHkiOmZhbHNlfSwibG9jYXRpb24iOnsiTG9uZ2l0dWRlIjotNzkuMjA5MDU3OTU5MjU3LCJMYXRpdHVkZSI6NDQuNTQ4MDE5MTg0MDY1LCJab29tIjoxNCwibWFwVmlld1R5cGUiOiJyb2FkbWFwIiwibWFwSW5mb1R5cGUiOltdLCJzZWxlY3RlZFBhdGhJRCI6IjZhMjk5Yzg2MDZmYWNhZjViNzUyOTNkOCIsIkJvdW5kcyI6eyJzb3V0aCI6NDQuNTMyODQ3NDU5OTA2NTQsIndlc3QiOi03OS4yMzY0ODA4NjQyMjUyNiwibm9ydGgiOjQ0LjU2MzE4Njk1NDcyMjQsImVhc3QiOi03OS4xODE2MzUwNTQyODg3M319LCJjb250cm9sU3BlY2lhbCI6e319";

const FALLBACK_LISTINGS = [
  { id: "12-30-laguna-parkway", title: "12 - 30 Laguna Parkway", price: "$489,000", beds: 3, baths: 4, sqft: "1,800–1,999 sq ft", days: "Listed 2 days ago", img: "/assets/listings/l1.jpg", url: "" },
  { id: "9-100-laguna-parkway", title: "9 - 100 Laguna Parkway", price: "$525,000", beds: 3, baths: 4, sqft: "1,800–1,999 sq ft", days: "Listed 2 days ago", img: "/assets/listings/l2.jpg", url: "" },
  { id: "41-paradise-boulevard", title: "41 Paradise Boulevard", price: "$899,000", beds: null, baths: null, sqft: "Vacant waterfront lot", days: "Listed 12 days ago", img: "/assets/listings/l3.jpg", url: "" },
  { id: "16-21-laguna-parkway", title: "16 - 21 Laguna Parkway", price: "$388,000", beds: 2, baths: 2, sqft: "1,200–1,399 sq ft", days: "Listed 13 days ago", img: "/assets/listings/l4.jpg", url: "" },
  { id: "10-2-paradise-boulevard", title: "Unit 10 - 2 Paradise Boulevard", price: "$399,649", beds: 2, baths: 2, sqft: "1,000–1,199 sq ft", days: "Listed 16 days ago", img: "/assets/listings/l5.jpg", url: "" },
  { id: "33-31-laguna-parkway", title: "Unit 33 - 31 Laguna Parkway", price: "$449,649", beds: 3, baths: 2, sqft: "1,400–1,599 sq ft", days: "Listed 16 days ago", img: "/assets/listings/l6.jpg", url: "" },
];

const listedLabel = (listedOn) => {
  if (!listedOn) return "For Sale";
  const days = Math.max(0, Math.round((Date.now() / 1000 - listed_on_safe(listedOn)) / 86400));
  if (days === 0) return "Listed today";
  if (days === 1) return "Listed yesterday";
  return `Listed ${days} days ago`;
};
const listed_on_safe = (v) => Number(v) || 0;

const mapApiListing = (d) => ({
  id: d.listing_id,
  slug: d.slug || d.listing_id,
  title: d.address,
  price: d.price ? `$${Number(d.price).toLocaleString("en-CA")}` : "",
  beds: d.beds || null,
  baths: d.baths || null,
  sqft: d.sqft ? (String(d.sqft).match(/\d/) && !String(d.sqft).toLowerCase().includes("ft") ? `${d.sqft} sq ft` : d.sqft) : "",
  days: listedLabel(d.listed_on),
  img: d.image,
  url: d.url || "",
});

export default function PropertyListings() {
  const [liveListings, setLiveListings] = useState(null);
  const [syncedAt, setSyncedAt] = useState(null);

  useEffect(() => {
    api
      .get("/listings")
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length) {
          setLiveListings(res.data.map(mapApiListing));
        }
      })
      .catch(() => {});
    api
      .get("/listings/meta")
      .then((res) => setSyncedAt(res.data?.refreshed_at || null))
      .catch(() => {});
  }, []);

  const listings = liveListings || FALLBACK_LISTINGS;

  return (
    <section id="properties" data-testid="properties-section" className="relative pt-28 pb-12 sm:pt-36 sm:pb-16 bg-[#0D0B0E]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14"
        >
          <div>
            <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#E890A7]/80 mb-4">01 · Portfolio</p>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight">
              Featured <em className="italic text-[#E890A7]">listings</em>
            </h2>
          </div>
          <p data-testid="listings-sync-note" className="text-sm text-slate-500 font-light max-w-xs md:text-right">
            {liveListings
              ? `Synced automatically from the live Lagoon City search${
                  syncedAt ? ` · updated ${new Date(syncedAt).toLocaleDateString("en-CA", { month: "short", day: "numeric" })}` : ""
                }`
              : "Fresh from the live Lagoon City search — every card opens Joselle's full MLS® map."}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {listings.map((l, i) => (
            <motion.article
              key={l.id}
              data-testid={`property-card-${l.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.08 }}
              className={`group relative rounded-3xl overflow-hidden border border-[#E890A7]/12 bg-[#151218] hover:border-[#E890A7]/40 transition-colors duration-500 ${
                i === 0 ? "md:col-span-2 md:row-span-2" : ""
              }`}
            >
              <div className={`overflow-hidden ${i === 0 ? "h-72 md:h-[26rem]" : "h-56"}`}>
                <img
                  src={l.img}
                  alt={l.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s] ease-out"
                />
              </div>
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0D0B0E] via-[#0D0B0E]/70 to-transparent p-6 pt-20">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-block rounded-full border border-emerald-400/30 bg-emerald-400/15 text-emerald-300 px-3 py-1 text-[10px] font-mono tracking-widest uppercase">
                    For Sale
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border border-[#E890A7]/25 bg-[#0D0B0E]/60 text-[#F2A7BA] px-3 py-1 text-[10px] font-mono tracking-widest uppercase">
                    <Clock3 size={10} /> {l.days}
                  </span>
                </div>
                <h3 className="font-display text-2xl sm:text-3xl">
                  {l.slug ? (
                    <Link data-testid={`property-title-link-${l.id}`} to={`/listings/${l.slug}`} className="hover:text-[#F2A7BA] transition-colors">
                      {l.title}
                    </Link>
                  ) : (
                    l.title
                  )}
                </h3>
                <p className="text-[10px] font-mono tracking-widest uppercase text-slate-500 mt-1">
                  Ramara · Lagoon City, Ontario
                </p>
                <p className="text-[#F2A7BA] font-semibold mt-1.5">{l.price}</p>
                <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                  {l.beds ? (
                    <span className="flex items-center gap-1.5">
                      <BedDouble size={14} /> {l.beds} bd
                    </span>
                  ) : null}
                  {l.baths ? (
                    <span className="flex items-center gap-1.5">
                      <Bath size={14} /> {l.baths} ba
                    </span>
                  ) : null}
                  {l.sqft ? (
                    <span className="flex items-center gap-1.5">
                      <Ruler size={14} /> {l.sqft}
                    </span>
                  ) : null}
                </div>
              </div>
              {l.slug ? (
                <Link
                  data-testid={`property-view-btn-${l.id}`}
                  to={`/listings/${l.slug}`}
                  className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#0D0B0E]/70 backdrop-blur-md border border-[#E890A7]/30 flex items-center justify-center text-[#F2A7BA] opacity-0 group-hover:opacity-100 hover:bg-[#E890A7] hover:text-[#0D0B0E] transition-all duration-300"
                  aria-label={`View details for ${l.title}`}
                >
                  <ArrowUpRight size={16} />
                </Link>
              ) : (
                <a
                  data-testid={`property-view-btn-${l.id}`}
                  href={l.url || LIVE_LISTINGS_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="absolute top-4 right-4 w-10 h-10 rounded-full bg-[#0D0B0E]/70 backdrop-blur-md border border-[#E890A7]/30 flex items-center justify-center text-[#F2A7BA] opacity-0 group-hover:opacity-100 hover:bg-[#E890A7] hover:text-[#0D0B0E] transition-all duration-300"
                  aria-label={`View ${l.title} on the live search`}
                >
                  <ArrowUpRight size={16} />
                </a>
              )}
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.8 }}
          className="mt-10 rounded-[2rem] border border-[#E890A7]/25 bg-gradient-to-r from-[#1E1A23] to-[#151218] p-8 sm:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6"
          data-testid="live-listings-cta"
        >
          <div>
            <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E890A7] mb-2">Live from the MLS®</p>
            <h3 className="font-display text-2xl sm:text-3xl">See every live Lagoon City listing</h3>
            <p className="text-sm text-slate-400 mt-2 font-light max-w-lg">
              The highlights above are a taste — browse Joselle&apos;s full, always-current Lagoon City
              search with an interactive map.
            </p>
          </div>
          <a
            data-testid="live-listings-btn"
            href={LIVE_LISTINGS_URL}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-3.5 flex items-center gap-2 hover:bg-[#F2A7BA] hover:-translate-y-0.5 transition-all duration-300"
          >
            Open live listings <ArrowUpRight size={16} />
          </a>
        </motion.div>

        <div className="mt-5 text-center md:text-right">
          <Link
            data-testid="calculators-cta-link"
            to="/calculators"
            className="inline-flex items-center gap-2 text-sm text-[#F2A7BA] hover:text-[#E890A7] transition-colors"
          >
            <Calculator size={15} /> Estimate your payments — free mortgage &amp; land transfer calculators
            <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}
